import {ElementRef, Inject, Injectable, Input, NgZone, OnDestroy, OnInit, Renderer2, ViewChild} from '@angular/core';
import {geoJSON, LatLngBounds, Layer, layerGroup} from 'leaflet';
import {BehaviorSubject, combineLatest, Observable, Subscription, zip} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, withLatestFrom} from 'rxjs/operators';
import {HttpClient, HttpParams} from '@angular/common/http';
import {MapService} from '../../../shared/map.service';
import {AuthorizationService} from '../../../authorization/authorization.service';
import {MapLayersService} from '../../../shared/map-layers.service';
import {DashboardGeopointsService} from '../services/dashboard-geopoints.service';
import {DashboardRegionGeofenceSumService} from '../services/dashboard-region-geofence-sum.service';
import {
  ProjectBusinessLogicSyncFilterService
} from '../../../shared/project-business-logic-filter/project-business-logic-sync-filter.service';
import {DashboardGeofenceService} from '../services/dashboard-geofence.service';
import {geofenceType} from '../../../shared/constants/geofence-type.constant';
import {GeoItemId} from '../../../shared/types/geo-item-id.type';
import {GeofenceHelper} from '../../../shared/utils/geofence-helper';
import {GeopointFactoryService} from '../../../shared/geopoint-factory.service';
import {NavigationEnd, Router} from '@angular/router';
import {RouteLibraryService} from '../../../route/route-library.service';
import {RouteLibraryEntities, RouteLibraryEntity} from '../../../route/route-library-entity';
import {RegionGeofenceSumModel} from '../../../shared/models/region-geofence-sum.model';
import {GeopointModel} from '../../../shared/models/geopoint.model';
import {GeofenceModel} from '../../../shared/models/geofence.model';
import {MapPaneService} from '../../../shared/map-pane.service';
import {mapPane} from '../../../shared/models/map-pane.models';
import {ProjectBusinessLogicFilterModel} from '../../../shared/project-business-logic-filter/project-business-logic-filter.model';

@Injectable()
export abstract class DashboardLayersGeoBaseControlComponent implements OnInit, OnDestroy {
  public static readonly pageSize = 1000;
  useStorage = true;
  visibleItems: GeoItemId[] = [];
  @Input() map: L.Map;
  @Input() mapGeoDataParams: MapGeoDataParams | null = null;
  minZoomForGeopoints = 14;
  mapZoom: BehaviorSubject<number> = new BehaviorSubject<number | null>(null);
  leafletBoundaryBox = new BehaviorSubject<LatLngBounds>(null);
  @ViewChild('geoitemToggleContainer') geoitemToggleContainer: ElementRef;
  subscriptions: Array<Subscription> = [];
  syncMapGeoDataSubscription: Subscription | null = null;
  private currentGeopointLayers: Map<string | number, L.Layer> = new Map();
  private layerClickHandlers = new WeakMap<L.Layer, () => void>();
  readonly isEditMode = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(() => {
      const url = this.router.url;
      return url.includes('/edit') || url.includes('/create');
    }),
    startWith(
      this.router.url.includes('/edit') || this.router.url.includes('/create')
    ),
    distinctUntilChanged()
  );

  constructor(
    protected renderer: Renderer2,
    public http: HttpClient,
    protected authorizationService: AuthorizationService,
    public dashboardGeopointsService: DashboardGeopointsService,
    public mapLayersService: MapLayersService,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint,
    protected dashboardRegionGeofenceSumService: DashboardRegionGeofenceSumService,
    protected dashboardGeofenceService: DashboardGeofenceService,
    protected projectBusinessLogicSyncFilterService: ProjectBusinessLogicSyncFilterService,
    protected geopointFactoryService: GeopointFactoryService,
    protected router: Router,
    protected routeLibrary: RouteLibraryService,
    protected ngZone: NgZone,
    protected mapPaneService: MapPaneService
  ) {
    this.renderer.listen('window', 'click', (e: Event) => {
      if (this.geoitemToggleContainer !== undefined) {
        if (!this.geoitemToggleContainer.nativeElement.contains(e.target)) {
          this.mapLayersService.controlGeoObjectsVisibility.next(false);
        }
      }
    });
  }

  mapMoveEnd() {
    this.leafletBoundaryBox.next(this.map.getBounds());
  }

  mapZoomEnd() {
    this.mapZoom.next(this.map.getZoom());
  }

  private moveEndHandler = () => this.mapMoveEnd();
  private zoomEndHandler = () => this.mapZoomEnd();

  ngOnInit() {
    this.map.on('moveend', this.moveEndHandler);
    this.map.on('zoomend', this.zoomEndHandler);
    this.mapMoveEnd();
    this.mapZoomEnd();
    let layerGroupData = layerGroup([]);
    this.subscriptions.push(
      combineLatest([
        this.mapLayersService.chosenLeafletGeoLayers,
        this.isEditMode
      ]).pipe(
        debounceTime(100),
      ).subscribe(([result, isEditMode]) => {
        const newLayerGroupData = layerGroup(result.length > 0 ? result.filter(layer => layer !== null) : []);
        const currentLayers = layerGroupData.getLayers();
        currentLayers.forEach(layer => {
          if (!newLayerGroupData.hasLayer(layer)) {
            this.map.removeLayer(layer);
          }
        });
        newLayerGroupData.getLayers().forEach(layer => {

          if (!this.map.hasLayer(layer)) {
            this.map.addLayer(layer);
          }

          const meta = (layer as L.Layer).meta;

          const existingHandler = this.layerClickHandlers.get(layer);

          if (existingHandler) {
            layer.off('click', existingHandler);
          }

          if (meta && meta.clickable && !isEditMode) {

            const clickHandler = () => {
              this.ngZone.run(() => {
                this.onLayerClick(meta.id, meta.entity);
              });
            };

            this.layerClickHandlers.set(layer, clickHandler);

            layer.on('click', clickHandler);
          }
        });

        newLayerGroupData.getLayers()
          .slice()
          .sort((a, b) => GeofenceHelper.getLayerArea(b) - GeofenceHelper.getLayerArea(a))
          .forEach(layer => {
            if ((layer as any).bringToFront) {
              (layer as any).bringToFront();
            }
          });

        layerGroupData = newLayerGroupData;
      })
    );
    this.subscriptions.push(
      combineLatest([
        this.mapLayersService.geopointLayersGroup,
        this.isEditMode
      ]).pipe(debounceTime(100))
        .subscribe(([group, isEditMode]) => {

          if (!group) {
            return;
          }

          group.getLayers().forEach(layer => {
            const meta = (layer as L.Layer).meta;
            layer.off('click');

            if (isEditMode) {
              return;
            }

            if (meta && meta.clickable && meta.id && meta.entity) {
              layer.on('click', () => {
                this.ngZone.run(() => {
                  this.onLayerClick(meta.id, meta.entity);
                });
              });
            }
          });
        })
    );

    this.subscriptions.push(
      combineLatest(
        this.mapLayersService.leafletRegionsAll,
        this.mapLayersService.leafletSumOfGeofences,
        this.mapLayersService.geoitemVisibility,
        this.mapLayersService.leafletGeofencesRideAllow,
        this.mapLayersService.leafletGeofencesRideRestrict,
        this.mapLayersService.leafletGeofencesFinishAllow,
        this.mapLayersService.leafletGeofencesFinishRestrict,
        this.mapLayersService.leafletGeofencesTechWorkArea,
        this.mapLayersService.leafletGeofencesSpeedLimit,
        this.mapLayersService.leafletGeofencesDemand,
      ).pipe(
        debounceTime(100),
        map(
          ([
             regionsAll,
             leafletSumOfGeofences,
             geoitemVisibility,
             leafletGeofencesRideAllow,
             leafletGeofencesRideRestrict,
             leafletGeofencesFinishAllow,
             leafletGeofencesFinishRestrict,
             leafletGeofencesTechWorkArea,
             leafletGeofencesSpeedLimit,
             leafletGeofencesDemand,
           ]) => {
            let layers = [];
            if (geoitemVisibility.regions) {
              layers = [...layers, ...regionsAll];
            }
            if (geoitemVisibility.sum_of_geofences) {
              layers = [...layers, ...leafletSumOfGeofences];
            }
            if (geoitemVisibility.ride_allow) {
              layers = [...layers, ...leafletGeofencesRideAllow];
            }
            if (geoitemVisibility.ride_restrict) {
              layers = [...layers, ...leafletGeofencesRideRestrict];
            }
            if (geoitemVisibility.finish_allow) {
              layers = [...layers, ...leafletGeofencesFinishAllow];
            }
            if (geoitemVisibility.finish_restrict) {
              layers = [...layers, ...leafletGeofencesFinishRestrict];
            }
            if (geoitemVisibility.tech_work_area) {
              layers = [...layers, ...leafletGeofencesTechWorkArea];
            }
            if (geoitemVisibility.speed_limit) {
              layers = [...layers, ...leafletGeofencesSpeedLimit];
            }
            if (geoitemVisibility.demand) {
              layers = [...layers, ...leafletGeofencesDemand];
            }
            return layers.filter(layer => layer !== null);
          }
        ),
      ).subscribe(result => {
        this.mapLayersService.chosenLeafletGeoLayers.next(result);
      }));
    this.mapPaneService.initPanes(this.map);
    this.subscriptions.push(
      this.mapLayersService.geopointLayersGroup.subscribe((group) => {
        if (!group) {
          return;
        }

        const newLayersMap = new Map<string | number, any>();
        group.getLayers().forEach((layer: any) => {
          if (layer.meta && layer.meta.id) {
            newLayersMap.set(layer.meta.id, layer);
          }
        });

        this.currentGeopointLayers.forEach((layer, id) => {
          const newLayer = newLayersMap.get(id);
          if (!newLayer || newLayer !== layer) {
            this.map.removeLayer(layer);
          }
        });

        newLayersMap.forEach((layer) => {
          if (!this.map.hasLayer(layer)) {
            this.map.addLayer(layer);
          }
        });

        this.currentGeopointLayers = newLayersMap;
      })
    );
    this.subscriptions.push(
      combineLatest(
        this.mapZoom,
        this.mapLayersService.geopointPane,
        this.mapLayersService.geoitemVisibility
      ).subscribe(
        ([zoom, pane, geoitemVisibility]) => {
          if (geoitemVisibility.geopoints && zoom > this.minZoomForGeopoints) {
            pane.style.display = '';
          } else {
            pane.style.display = 'none';
          }
        }
      )
    );
  }

  abstract getRefreshTrigger(): Observable<[number, LatLngBounds, ProjectBusinessLogicFilterModel]>;

  getMapGeoData() {
    const extraParams = this.mapGeoDataParams;
    return this.getRefreshTrigger().pipe(
      switchMap(([_, leafletBoundaryBox, projectBusinessLogicFilter]) => {
        return zip(
          this.dashboardGeofenceService.getItems(leafletBoundaryBox, projectBusinessLogicFilter, extraParams),
          this.dashboardGeopointsService.getItems(leafletBoundaryBox, projectBusinessLogicFilter, extraParams),
          this.dashboardRegionGeofenceSumService.getItems(leafletBoundaryBox, projectBusinessLogicFilter, extraParams),
        );
      })
    );
  }

  loadMapGeoData(enableLayerClick: boolean) {
    if (this.syncMapGeoDataSubscription) {
      this.syncMapGeoDataSubscription.unsubscribe();
    }

    this.syncMapGeoDataSubscription = this.getMapGeoData().subscribe(([geofences, geopoints, regionGeofences]) => {
      this.handleGeoDataResponse([geofences, geopoints, regionGeofences], enableLayerClick);
    });
  }

  private handleGeoDataResponse(
    [geofences, geopoints, regionGeofences]:
    [GeofenceModel[], GeopointModel[], RegionGeofenceSumModel[]],
    enableLayerClick: boolean) {
    this.processGeofences(geofences, enableLayerClick);
    this.processGeopoints(geopoints, enableLayerClick);
    this.processRegionGeofence(regionGeofences, enableLayerClick);
    this.processRegionGeofenceSum(regionGeofences);
  }

  processGeofences(data: GeofenceModel[], enableLayerClick = false) {
    const geofences = {};
    Object.values(geofenceType).forEach(type => {
      geofences[type] = [];
    });

    data.forEach((geofence: GeofenceModel) => {
      const layer = GeofenceHelper.createGeofenceLayer(geofence).layer;
      layer.meta = {
        entity: RouteLibraryEntities.GEO_OBJECTS_GEOFENCE,
        id: geofence.id,
        clickable: enableLayerClick
      };
      layer.options.pane = this.mapPaneService.getPaneName(geofence.type);
      geofences[geofence.type].push(layer);
    });
    this.mapLayersService.leafletGeofencesRideAllow.next(geofences[geofenceType.RIDE_ALLOW]);
    this.mapLayersService.leafletGeofencesRideRestrict.next(geofences[geofenceType.RIDE_RESTRICT]);
    this.mapLayersService.leafletGeofencesFinishAllow.next(geofences[geofenceType.FINISH_ALLOW]);
    this.mapLayersService.leafletGeofencesFinishRestrict.next(geofences[geofenceType.FINISH_RESTRICT]);
    this.mapLayersService.leafletGeofencesTechWorkArea.next(geofences[geofenceType.TECH_WORK_AREA]);
    this.mapLayersService.leafletGeofencesSpeedLimit.next(geofences[geofenceType.SPEED_LIMIT]);
    this.mapLayersService.leafletGeofencesDemand.next(geofences[geofenceType.DEMAND]);
  }

  processGeopoints(data: GeopointModel[], enableLayerClick = false) {
    const group = layerGroup([], {pane: mapPane.GEOPOINT});

    data.forEach(geopoint => {
      const layer = this.geopointFactoryService.createGeopointLayer(geopoint).layer;
      layer.meta = {
        entity: RouteLibraryEntities.GEO_OBJECTS_GEOPOINT,
        id: geopoint.id,
        clickable: enableLayerClick
      };
      group.addLayer(layer);
    });
    this.mapLayersService.geopointLayersGroup.next(group);
  }

  private onLayerClick = (id: number, entity: RouteLibraryEntity) => {
    this.router.navigate([
      this.routeLibrary.getRoute({
        entity: entity,
        action: 'view',
        id: id
      })
    ]);
  }

  processRegionGeofenceSum(data: RegionGeofenceSumModel[]) {
    this.mapLayersService.leafletSumOfGeofences.next(
      data
        .map(
          row => {
            let layer: Layer | null = null;
            switch (row.type) {
              case geofenceType.RIDE_ALLOW:
                layer = geoJSON(JSON.parse(row.geometry), {
                  style: GeofenceHelper.getColor(geofenceType.RIDE_ALLOW),
                  pane: mapPane.RIDE_ALLOW
                });
                break;
              case geofenceType.FINISH_ALLOW:
                layer = geoJSON(JSON.parse(row.geometry), {
                  style: GeofenceHelper.getColor(geofenceType.FINISH_ALLOW),
                  pane: mapPane.FINISH_ALLOW
                });
                break;
            }
            if (layer) {
              layer.bindPopup(row.name);
            }
            return layer;
          }
        )
        .filter((layer: Layer | null) => !!layer && (!('getLayers' in layer) || (layer as any).getLayers().length > 0))
    );
  }

  processRegionGeofence(data: RegionGeofenceSumModel[], enableLayerClick = false) {
    const layers = data
      .filter(row => row.type === 'region')
      .map(row => {
        const layer = geoJSON(JSON.parse(row.geometry), MapService.leafletRegionsOptions);
        layer.bindPopup(row.name);
        layer.meta = {
          entity: RouteLibraryEntities.GEO_OBJECTS_REGION,
          id: row.region_id,
          clickable: enableLayerClick
        };

        return layer;
      })
      .filter((layer: Layer | null) => !!layer && (!('getLayers' in layer) || (layer as any).getLayers().length > 0));

    this.mapLayersService.leafletRegionsAll.next(layers);
  }

  getRegions(params: HttpParams) {
    this.http.get<Array<any>>(
      this.environmentApiBasepoint + 'region',
      {
        observe: 'body',
        params: params
      }
    )
      .pipe(
        withLatestFrom(this.authorizationService.isRootOrSuperuser())
      )
      .subscribe(
        ([data, isRootOrSuperuser]) => {
          const regions = [];
          data.forEach((region) => {
            const boundaryLayer = geoJSON(JSON.parse(region.boundary), MapService.leafletRegionsOptions);
            boundaryLayer.bindPopup(region.name);
            const rideAllowLayer = geoJSON(JSON.parse(isRootOrSuperuser ? region.ride_allow_enlarged : region.ride_allow), {
              style: GeofenceHelper.getColor(geofenceType.RIDE_ALLOW),
              pane: mapPane.RIDE_ALLOW
            });
            const finishAllowLayer = geoJSON(JSON.parse(isRootOrSuperuser ? region.finish_allow_enlarged : region.finish_allow), {
              style: GeofenceHelper.getColor(geofenceType.FINISH_ALLOW),
              pane: mapPane.FINISH_ALLOW
            });
            regions.push(boundaryLayer, rideAllowLayer, finishAllowLayer);
          });
          this.mapLayersService.leafletRegionsAll.next(regions);
        }
      );
  }

  ngOnDestroy() {
    this.geoitemToggleContainer = undefined;
    this.mapLayersService.deleteAllLayers();
    this.mapLayersService.controlGeoObjectsVisibility.next(false);
    this.subscriptions.filter(s => !!s).forEach(s => s.unsubscribe());
    if (this.syncMapGeoDataSubscription) {
      this.syncMapGeoDataSubscription.unsubscribe();
    }
    this.map.off('moveend', this.moveEndHandler);
    this.map.off('zoomend', this.zoomEndHandler);
  }
}
